/**
 * Unified Auth Context that works on both web and iOS
 * - Web: Uses react-oidc-context
 * - iOS: Uses our custom iOS auth service with OAuth2Client
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { Capacitor } from '@capacitor/core';
import * as iosAuth from '../services/iosAuth';
import { TokenData, getTokens } from '../services/tokenStorage';
import { setWebAuthContext } from '../services/api';

interface AuthContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  user: {
    access_token: string;
    id_token: string;
  } | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

function isIOS(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

// Safe wrapper for useOidcAuth that returns a stub on iOS
function useSafeOidcAuth() {
  // On iOS, we don't use the web auth provider, so return a stub
  if (isIOS()) {
    return {
      isLoading: false,
      isAuthenticated: false,
      error: null,
      user: null,
      signinRedirect: async () => {},
      signoutRedirect: async () => {},
    };
  }
  
  // On web, use the real OIDC auth
  return useOidcAuth();
}

export function PlatformAuthProvider({ children }: AuthProviderProps) {
  const webAuth = useSafeOidcAuth();
  const [iosLoading, setIosLoading] = useState(true);
  const [iosAuthenticated, setIosAuthenticated] = useState(false);
  const [iosUser, setIosUser] = useState<TokenData | null>(null);
  const [iosError, setIosError] = useState<Error | null>(null);

  // On web, set the auth context for API service
  // Note: Token sync to chrome.storage is handled by the extension's content script
  // running on the Portal page, which reads from sessionStorage/localStorage
  useEffect(() => {
    if (!isIOS() && webAuth.isAuthenticated) {
      setWebAuthContext(webAuth);
    }
  }, [webAuth, webAuth.isAuthenticated]);

  // On iOS, check authentication status on mount
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    console.log('[Platform Auth] Platform detected:', platform);
    
    if (!isIOS()) {
      console.log('[Platform Auth] Not iOS, skipping native auth check');
      // On web, set loading to false immediately since we use webAuth
      setIosLoading(false);
      return;
    }

    console.log('[Platform Auth] iOS detected, checking authentication...');
    
    async function checkAuth() {
      try {
        setIosLoading(true);
        console.log('[Platform Auth] Calling isAuthenticated()...');
        const authenticated = await iosAuth.isAuthenticated();
        console.log('[Platform Auth] isAuthenticated result:', authenticated);
        setIosAuthenticated(authenticated);
        
        if (authenticated) {
          console.log('[Platform Auth] Getting stored tokens...');
          const tokens = await getTokens();
          if (tokens) {
            console.log('[Platform Auth] Tokens found, setting user');
            setIosUser(tokens);
          }
        }
      } catch (error) {
        console.error('[Platform Auth] Error checking iOS auth status:', error);
        setIosError(error as Error);
      } finally {
        console.log('[Platform Auth] Setting iosLoading to false');
        setIosLoading(false);
      }
    }

    checkAuth();
  }, []);

  // iOS auth handlers
  const iosSignIn = async () => {
    try {
      setIosLoading(true);
      setIosError(null);
      const tokens = await iosAuth.mobileSignIn();
      setIosUser(tokens);
      setIosAuthenticated(true);
    } catch (error) {
      console.error('[Platform Auth] iOS sign-in error:', error);
      setIosError(error as Error);
      throw error;
    } finally {
      setIosLoading(false);
    }
  };

  const iosSignOut = async () => {
    try {
      await iosAuth.mobileSignOut();
      setIosUser(null);
      setIosAuthenticated(false);
    } catch (error) {
      console.error('[Platform Auth] iOS sign-out error:', error);
      setIosError(error as Error);
      throw error;
    }
  };

  // Build the context value based on platform
  const value: AuthContextValue = isIOS()
    ? {
        isLoading: iosLoading,
        isAuthenticated: iosAuthenticated,
        error: iosError,
        user: iosUser ? {
          access_token: iosUser.access_token,
          id_token: iosUser.id_token,
        } : null,
        signIn: iosSignIn,
        signOut: iosSignOut,
        getAccessToken: iosAuth.getAccessToken,
        getIdToken: iosAuth.getIdToken,
      }
    : {
        isLoading: webAuth.isLoading,
        isAuthenticated: webAuth.isAuthenticated,
        error: (webAuth.error as Error) || null,
        user: webAuth.user ? {
          access_token: webAuth.user.access_token || '',
          id_token: webAuth.user.id_token || '',
        } : null,
        signIn: async () => {
          await webAuth.signinRedirect();
        },
        signOut: async () => {
          // AWS Cognito doesn't follow standard OIDC logout, so we manually redirect
          const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
          const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
          const logoutUri = window.location.origin;
          
          // Clear local session storage
          sessionStorage.clear();
          localStorage.removeItem('accountType');
          
          // Redirect to Cognito's logout endpoint with correct parameters
          const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
          window.location.href = logoutUrl;
        },
        getAccessToken: async () => webAuth.user?.access_token || null,
        getIdToken: async () => webAuth.user?.id_token || null,
      };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function usePlatformAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('usePlatformAuth must be used within PlatformAuthProvider');
  }
  return context;
}

// Re-export TokenData type for convenience
export type { TokenData } from '../services/tokenStorage';

